import { useSubconfigurationNavigationApi } from "../CustomSubconfigurationNavigation/SubconfigurationNavigationApi";

import { type JSX, ReactNode, useEffect, useRef, useState } from "react";

import CardActionArea from "@mui/material/CardActionArea";
import {
  useCsAttribute,
  useCsAttributeApi,
  useCsValue,
  useLocalOptionSelected,
} from "@plansoft-configuration-renderer/framework";

/**
 * Props for {@linkcode ThumbnailActionArea}
 * @category UI
 */
export interface ThumbnailActionAreaProps {
  children?: ReactNode;
  nextAfterSelection?: boolean;
}

/**
 * Renders basic wrapper around the content
 *
 * @category UI
 */
export function ThumbnailActionArea(
  props: ThumbnailActionAreaProps,
): JSX.Element {
  const value = useCsValue();
  const attribute = useCsAttribute();
  const { toggleOption } = useCsAttributeApi();
  const { next, nextDisabled } = useSubconfigurationNavigationApi();

  const isSelected = useLocalOptionSelected();

  // Counts manual selections the engine has answered; the effect below runs
  // in the render after the response, when isSelected/nextDisabled are current.
  const [answeredSelections, setAnsweredSelections] = useState(0);
  const handledSelections = useRef(0);

  useEffect(() => {
    if (answeredSelections === handledSelections.current) {
      return;
    }
    handledSelections.current = answeredSelections;
    // The engine may have rejected the selection or kept the next step
    // disabled - then stay, rather than jumping later on.
    if (isSelected && !nextDisabled) {
      next();
    }
  }, [answeredSelections, isSelected, nextDisabled, next]);

  const isDisabled = Boolean(
    attribute.isReadOnly ||
    !attribute.isAllowed ||
    !value.isAllowed ||
    !value.isActive,
  );

  const handleClick = async (): Promise<void> => {
    const isSelecting = !isSelected;
    await toggleOption(value.value, isSelecting);
    if (isSelecting && props.nextAfterSelection === true) {
      setAnsweredSelections((count) => count + 1);
    }
  };

  return (
    <CardActionArea
      component="div"
      disabled={isDisabled}
      sx={{ height: "100%" }}
      onClick={handleClick}
    >
      {props.children}
    </CardActionArea>
  );
}
